import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '@material-ui/core/Badge';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import NotificationsOutlinedIcon from '@material-ui/icons/NotificationsOutlined';
import {
  configApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api';
import {
  iconForType,
  resourceRoute,
  formatRelativeTime,
} from '../notificationDisplay';
import { Notification } from '../types';

const UNREAD_POLL_INTERVAL_MS = 10000; // matches RequestsPage.tsx's own polling convention
const DROPDOWN_PREVIEW_LIMIT = 8;

export const NotificationBell = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const baseUrl = configApi.getString('notifications.baseUrl');

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const refresh = () => {
      getUnreadCount(fetchApi, identityApi, baseUrl)
        .then(count => {
          if (mountedRef.current) setUnreadCount(count);
        })
        .catch(() => {
          /* best-effort — a transient failure just leaves the last-known
           * badge count showing until the next successful poll. */
        });
    };
    refresh();
    const interval = setInterval(refresh, UNREAD_POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchApi, identityApi, baseUrl]);

  const loadPreview = () => {
    setLoading(true);
    listNotifications(fetchApi, identityApi, baseUrl, 1, DROPDOWN_PREVIEW_LIMIT)
      .then(result => {
        setItems(result.items);
        setUnreadCount(result.unreadCount);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadPreview();
  };
  const close = () => setAnchorEl(null);

  const handleMarkAllRead = () => {
    markAllNotificationsRead(fetchApi, identityApi, baseUrl)
      .then(() => {
        setItems(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
      })
      .catch(() => {
        /* best-effort — leave state as-is on failure, user can retry */
      });
  };

  const handleItemClick = (notification: Notification) => {
    if (!notification.isRead) {
      markNotificationRead(
        fetchApi,
        identityApi,
        baseUrl,
        notification.id,
      ).catch(() => {
        /* best-effort — navigation should still happen even if this fails */
      });
      setItems(prev =>
        prev.map(n => (n.id === notification.id ? { ...n, isRead: true } : n)),
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    close();
    const route = resourceRoute(
      notification.resourceType,
      notification.resourceId,
    );
    if (route) navigate(route);
  };

  return (
    <>
      <IconButton size="small" aria-label="Notifications" onClick={open}>
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          overlap="circle"
          invisible={unreadCount === 0}
        >
          <NotificationsOutlinedIcon fontSize="small" />
        </Badge>
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box
          style={{
            width: 380,
            maxHeight: 480,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ padding: '12px 16px' }}
          >
            <Typography variant="subtitle2">
              <strong>Notifications</strong>
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={handleMarkAllRead}
                style={{ textTransform: 'none' }}
              >
                Mark all as read
              </Button>
            )}
          </Box>
          <Divider />
          <Box style={{ overflowY: 'auto', flex: 1 }}>
            {loading && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: 16 }}
              >
                Loading…
              </Typography>
            )}
            {!loading && items.length === 0 && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: 16 }}
              >
                No notifications yet.
              </Typography>
            )}
            {!loading &&
              items.map(notification => {
                const { icon } = iconForType(notification.type);
                return (
                  <Box
                    key={notification.id}
                    onClick={() => handleItemClick(notification)}
                    display="flex"
                    style={{
                      gap: 10,
                      padding: '10px 16px',
                      cursor: 'pointer',
                      backgroundColor: notification.isRead
                        ? undefined
                        : theme.palette.action.hover,
                      borderBottom: `1px solid ${theme.palette.border}`,
                    }}
                  >
                    <Box
                      style={{
                        color: theme.palette.text.secondary,
                        marginTop: 2,
                      }}
                    >
                      {icon}
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {notification.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatRelativeTime(notification.createdAt)}
                      </Typography>
                    </Box>
                    {!notification.isRead && (
                      <Box
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          flexShrink: 0,
                          marginTop: 6,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
          </Box>
          <Divider />
          <Button
            fullWidth
            size="small"
            onClick={() => {
              close();
              navigate('/notifications');
            }}
            style={{
              textTransform: 'none',
              padding: '10px 16px',
              justifyContent: 'flex-start',
            }}
          >
            View all notifications →
          </Button>
        </Box>
      </Popover>
    </>
  );
};
